import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldX, Users } from 'lucide-react';

interface Supervisor {
  user_id: string;
  email: string;
  assignedCount: number;
}

interface BulkPatientUnassignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPatientIds: string[];
  onSuccess: () => void;
}

export function BulkPatientUnassignDialog({
  open,
  onOpenChange,
  selectedPatientIds,
  onSuccess,
}: BulkPatientUnassignDialogProps) {
  const { user } = useAdminAuth();
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingSupervisors, setLoadingSupervisors] = useState(true);

  useEffect(() => {
    if (open) {
      loadSupervisorsWithAssignments();
      setSelectedSupervisor('');
    }
  }, [open, selectedPatientIds]);

  const loadSupervisorsWithAssignments = async () => {
    if (selectedPatientIds.length === 0) return;

    setLoadingSupervisors(true);
    try {
      // Get all assignments for the selected patients
      const { data: assignments } = await supabase
        .from('patient_assignments')
        .select('staff_user_id, patient_id')
        .in('patient_id', selectedPatientIds)
        .is('revoked_at', null);

      if (!assignments || assignments.length === 0) {
        setSupervisors([]);
        setLoadingSupervisors(false);
        return;
      }

      // Count assignments per supervisor
      const supervisorCounts: Record<string, number> = {};
      assignments.forEach(a => {
        supervisorCounts[a.staff_user_id] = (supervisorCounts[a.staff_user_id] || 0) + 1;
      });

      // Get supervisor emails
      const supervisorIds = Object.keys(supervisorCounts);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email')
        .in('user_id', supervisorIds);

      const supervisorList = (profiles || []).map(p => ({
        user_id: p.user_id,
        email: p.email,
        assignedCount: supervisorCounts[p.user_id] || 0,
      }));

      setSupervisors(supervisorList);
    } catch (error) {
      console.error('Error loading supervisors:', error);
      toast.error('Failed to load supervisors');
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const handleUnassign = async () => {
    if (!selectedSupervisor || selectedPatientIds.length === 0) return;

    setLoading(true);
    try {
      // Revoke assignments for the selected patients from this supervisor
      const { error } = await supabase
        .from('patient_assignments')
        .update({ revoked_at: new Date().toISOString() })
        .eq('staff_user_id', selectedSupervisor)
        .in('patient_id', selectedPatientIds)
        .is('revoked_at', null);

      if (error) throw error;

      const supervisor = supervisors.find(s => s.user_id === selectedSupervisor);
      toast.success(`Removed ${supervisor?.assignedCount || 0} patient assignments from ${supervisor?.email || 'supervisor'}`);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error unassigning patients:', error);
      toast.error('Failed to unassign patients');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldX className="h-5 w-5 text-destructive" />
            Unassign Patients from Supervisor
          </DialogTitle>
          <DialogDescription>
            Remove supervisor assignments from {selectedPatientIds.length} selected patient{selectedPatientIds.length !== 1 ? 's' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            <Users className="h-4 w-4" />
            <span>{selectedPatientIds.length} patient{selectedPatientIds.length !== 1 ? 's' : ''} selected</span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supervisor">Select Supervisor to Unassign</Label>
            <Select
              value={selectedSupervisor}
              onValueChange={setSelectedSupervisor}
              disabled={loadingSupervisors}
            >
              <SelectTrigger id="supervisor">
                <SelectValue placeholder={loadingSupervisors ? 'Loading...' : 'Choose a supervisor'} />
              </SelectTrigger>
              <SelectContent>
                {supervisors.map((supervisor) => (
                  <SelectItem key={supervisor.user_id} value={supervisor.user_id}>
                    {supervisor.email} ({supervisor.assignedCount} assigned)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingSupervisors && supervisors.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No supervisors are assigned to the selected patients.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleUnassign}
            disabled={!selectedSupervisor || loading || supervisors.length === 0}
          >
            {loading ? 'Removing...' : 'Remove Assignments'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
